import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import type { IntuneIntegration, MosyleIntegration } from '../lib/types';
import { CopyField, DataSourcePicker, useIntegrationMutations } from './integration-shell';
import { Instructions, RemoveCredentials, Step, useCredentials } from './mdm-integrations';
import { Alert, Button, Field, Input } from './ui';

// ---------------------------------------------------------------------------
// Mosyle
// ---------------------------------------------------------------------------

export function MosyleConnection({ integration }: { integration: MosyleIntegration }) {
  const { can } = useAuth();
  const isAdmin = can('ADMIN');
  const { save, remove } = useCredentials('MOSYLE');
  const saved = integration.mosyle;
  const [accessToken, setAccessToken] = useState('');
  const [email, setEmail] = useState(saved.email ?? '');
  const [password, setPassword] = useState('');

  return (
    <div className="space-y-8">
      <section>
        <h3 className="mb-3 text-sm font-semibold text-slate-900">Data source</h3>
        <DataSourcePicker integration={integration} />
      </section>

      {integration.mode === 'LIVE' && (
        <Step number={1} title="API access">
          <Instructions
            steps={[
              <>
                In Mosyle Manager, go to <strong>My School › Integrations › API Integration</strong>, turn on API access, and copy the <strong>access token</strong>.
              </>,
              <>
                Choose a Mosyle admin that’s allowed to use the API. Mosyle exchanges that admin’s email and password for the sign-in token Edvora uses on
                every request.
              </>,
            ]}
          />
          {saved.secretsUnreadable && <Alert tone="red">The saved credentials can’t be read. Enter them again.</Alert>}
          <div className="grid max-w-2xl gap-4 sm:grid-cols-2">
            <Field label="Access token" className="sm:col-span-2">
              <Input
                type="password"
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
                disabled={!isAdmin}
                placeholder={saved.hasAccessToken ? 'Saved - leave blank to keep' : ''}
                className="font-mono text-xs"
              />
            </Field>
            <Field label="Admin email">
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={!isAdmin} />
            </Field>
            <Field label="Admin password">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={!isAdmin}
                placeholder={saved.hasPassword ? 'Saved - leave blank to keep' : ''}
              />
            </Field>
          </div>
          {isAdmin && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                loading={save.isPending}
                onClick={() =>
                  save.mutate(
                    { email, ...(accessToken ? { accessToken } : {}), ...(password ? { password } : {}) },
                    {
                      onSuccess: () => {
                        setAccessToken('');
                        setPassword('');
                      },
                    },
                  )
                }
              >
                Save API access
              </Button>
              {(saved.email || saved.hasAccessToken) && <RemoveCredentials provider="MOSYLE" remove={remove} />}
            </div>
          )}
        </Step>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Microsoft Intune
// ---------------------------------------------------------------------------

export function IntuneConnection({ integration }: { integration: IntuneIntegration }) {
  const { can } = useAuth();
  const isAdmin = can('ADMIN');
  const { update } = useIntegrationMutations('INTUNE');
  const { save, remove } = useCredentials('INTUNE');
  const saved = integration.intune;
  const [directoryId, setDirectoryId] = useState(integration.settings.directoryId ?? '');
  const [clientId, setClientId] = useState(saved.clientId ?? '');
  const [clientSecret, setClientSecret] = useState('');
  useEffect(() => setDirectoryId(integration.settings.directoryId ?? ''), [integration.settings.directoryId]);

  return (
    <div className="space-y-8">
      <section>
        <h3 className="mb-3 text-sm font-semibold text-slate-900">Data source</h3>
        <DataSourcePicker integration={integration} />
      </section>

      {integration.mode === 'LIVE' && (
        <>
          <Step number={1} title="App registration">
            <Instructions
              steps={[
                <>
                  In the Microsoft Entra admin center, go to <strong>App registrations</strong> and create a <strong>New registration</strong> for your
                  organization only. It doesn’t need a redirect URI.
                </>,
                <>
                  Under <strong>API permissions</strong>, add the Microsoft Graph <strong>application</strong> permission below, then choose{' '}
                  <strong>Grant admin consent</strong>. Edvora only reads device inventory.
                </>,
                <>
                  Under <strong>Certificates &amp; secrets</strong>, create a client secret and copy its <strong>Value</strong>. Note when it expires.
                </>,
              ]}
            />
            <div className="max-w-2xl">
              <CopyField label="Microsoft Graph permission" value={saved.permission} />
            </div>
          </Step>

          <Step number={2} title="Directory and credentials">
            {saved.secretsUnreadable && <Alert tone="red">The saved credentials can’t be read. Enter them again.</Alert>}
            <div className="flex max-w-2xl flex-col gap-2 sm:flex-row sm:items-end">
              <div className="flex-1">
                <Field label="Directory (tenant) ID" hint="On the app registration’s Overview, or your onmicrosoft.com domain.">
                  <Input value={directoryId} onChange={(e) => setDirectoryId(e.target.value)} disabled={!isAdmin} className="font-mono text-xs" />
                </Field>
              </div>
              {isAdmin && (
                <Button loading={update.isPending} onClick={() => update.mutate({ settings: { directoryId } })}>
                  Save directory
                </Button>
              )}
            </div>
            <div className="grid max-w-2xl gap-4 sm:grid-cols-2">
              <Field label="Application (client) ID">
                <Input value={clientId} onChange={(e) => setClientId(e.target.value)} disabled={!isAdmin} className="font-mono text-xs" />
              </Field>
              <Field label="Client secret value">
                <Input
                  type="password"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  disabled={!isAdmin}
                  placeholder={saved.hasClientSecret ? 'Saved - leave blank to keep' : ''}
                />
              </Field>
            </div>
            {isAdmin && (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="primary"
                  loading={save.isPending}
                  onClick={() => save.mutate({ clientId, ...(clientSecret ? { clientSecret } : {}) }, { onSuccess: () => setClientSecret('') })}
                >
                  Save app credentials
                </Button>
                {saved.clientId && <RemoveCredentials provider="INTUNE" remove={remove} />}
              </div>
            )}
          </Step>
        </>
      )}
    </div>
  );
}
